/** @import ../../../common/types */
import Link from '@mui/material/Link'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import { masterClassification, restApiKey, table, titleName } from '../../../_constants/Code'
import { useCertificationSetTableHeaders, useCertificationSetResources } from './extra/useCertificationSet'
import { useModal, useModalExtra } from '../../../_components/Modal'

import CertificationSelector from '../../../common/utils/CertificationSelector'
import React, { useEffect, useRef, useState } from 'react'

import RelatedDashboard from '../parts/RelatedDashboard'
import { useSelector } from 'react-redux'
import { useToast } from '../../../_components/Toast'
import { useCertificationResources } from '../certificationInfo/extra/useCertificationInfo'
import { SpinnerButton } from '../../common/CustomButton'
import { httpPost } from '../../../services/api/AmplifyApi'
import { PathInfoList } from '../../../_constants/PathInfoList'
import { nanoid } from 'nanoid'
import { errorLog } from '../../../common/logger/Logger'
import { getMessage } from '../../../common/utils/MessageUtils'

import Forbidden from '../../../_components/Forbidden'

const delimiter = '、'

// 意図的に throw する業務エラー。catch 側でこれのみ表示、英語は E011012 へ（バグ #3228）
const CERT_SET_UPDATE_ERROR_MESSAGE = '更新処理でエラーが発生しました'

const _tableData = [
  // {
  //   setName: '重点資格1',
  //   setData: [
  //     'プロジェクトマネージャ試験',
  //     'ネットワークスペシャリスト試験',
  //     'データベーススペシャリスト試験',
  //     'エンベデッドシ'
  //   ]
  // },
  // {
  //   setName: '重点資格2',
  //   setData: ['応用情報技術者試験', '基本情報技術者試験', 'HITAゴールド']
  // }
]

const pageTitle = '資格関連マスター'

const CertificationSetContent = (props) => {
  const [isLoaded, setIsLoaded] = useState(false)
  const [master, setMaster] = useState([])

  const [certifications, setCertifications] = useState({})
  const [tableData, setTableData] = useState(_tableData)
  const [serverErrorMessage, setServerErrorMessage] = useState('')

  const { getMaster, getCertificationSets } = useCertificationSetResources()
  const { getCertifications } = useCertificationResources()
  const { _closeUpModal: closeModal } = useModalExtra()

  /** @type {ReturnType<typeof React.useRef<HTMLElement>>} */
  const errorMessageRef = useRef(null)

  const headers = useCertificationSetTableHeaders({
    onEdit: handleEditModalSubmit,
    onDelete: handleDelete,
    certifications
  })

  const init = async () => {
    document.title = props.pageTitle
    const [master, { certifications, certificationCodeMap }, certificationSets = []] = await Promise.all([
      getMaster(),
      getCertifications(),
      getCertificationSets()
    ])

    setMaster(master)

    const certificationTableData = certificationSets.map((x) => {
      const certificationCodeList = x.CertificationCodeList.map((e) => certificationCodeMap[e])
      return { ...x, CertificationCodeList: certificationCodeList }
    })

    setCertifications(certifications)
    setTableData(certificationTableData)
    setIsLoaded(true)
  }

  useEffect(() => {
    init()
  }, [])

  const handleAddSet = () => {
    props.showModal({
      title: '資格セット設定',
      description: '資格セットに紐づける資格を以下から選択してください',
      isWide: false,
      body: (
        <CertificationSelector
          key={Date.now()}
          certifications={certifications}
          onSave={addSet}
          checkIfCertificationSetNameExists={checkIfCertificationSetNameExists}
        />
      ),
      isCancel: false
    })
  }

  /**
   * @function addSet 資格セットを追加
   * @param {CertificationSetRow} selectorData
   */
  const addSet = (selectorData) => {
    const predicate = (state) => {
      const tempState = [...state]

      tempState.push({
        CertificationSetCode: nanoid(),
        CertificationSetName: selectorData.certificationSetName,
        CertificationCodeList: selectorData.certificationCodeList,
        CreateDateTime: null,
        CreateUserId: null,
        UpdateDateTime: null,
        UpdateUserId: null,
        deleted: false,
        edited: false
      })

      return tempState
    }

    const newState = predicate(tableData)
    setTableData(newState)

    closeModal()
  }

  /**
   * @function editSet 資格セットを追加
   * @param {CertificationSetRow} newData 編集データ
   * @param {CertificationSetRow} row 既存データ
   * @param {number} index
   */
  const editSet = (newData, row, index) => {
    const predicate = (state) => {
      const tempState = [...state]

      const edited =
        row.CertificationSetName !== newData.certificationSetName ||
        JSON.stringify(row.CertificationCodeList) !== JSON.stringify(newData.certificationCodeList)

      tempState[index] = {
        ...row,
        CertificationSetName: newData.certificationSetName,
        CertificationCodeList: newData.certificationCodeList,
        UpdateUserId: props.userId,
        edited
      }

      return tempState
    }

    const newState = predicate(tableData)
    setTableData(newState)

    closeModal()
  }

  /**
   * @function deleteSet 資格セットを削除
   * @param {number} index
   */
  const deleteSet = (index) => {
    const predicate = (state) => {
      const tempState = [...state]

      if (tempState[index].CreateDateTime !== null) {
        tempState[index].deleted = !tempState[index].deleted
      } else {
        tempState.splice(index, 1)
      }
      return tempState
    }

    const newState = predicate(tableData)

    setTableData(newState)
  }

  /**
   * @function handleEditModalSubmit 資格セットを削除
   * @param row
   * @param index
   */
  function handleEditModalSubmit(row, index) {
    const defaultValue = {
      certificationSetName: row.CertificationSetName,
      certificationCodeList: row.CertificationCodeList
    }
    props.showModal({
      title: '資格セット設定',
      description: '資格セットに紐づける資格を以下から選択してください',
      isCancel: false,
      isWide: false,
      body: (
        <CertificationSelector
          certifications={certifications}
          editMode
          onSave={(s) => editSet(s, row, index)}
          defaultValue={defaultValue}
          checkIfCertificationSetNameExists={(e) => checkIfCertificationSetNameExists(e, row.CertificationSetCode)}
        />
      )
    })
  }

  function handleDelete(index) {
    deleteSet(index)
  }

  /** @function checkIfCertificationSetNameExists 重複チェック */
  function checkIfCertificationSetNameExists(input, id) {
    return tableData.some((e) => {
      let compare = e.CertificationSetName === input
      if (id) compare &= id !== e.CertificationSetCode
      return compare
    })
  }

  /**
   * @param {Record<string, string | string[]>[]} tableData
   * @returns
   */

  function prepareData(tableData) {
    const payload = []

    for (let data of tableData) {
      let operationType = null

      if (data.CreateDateTime === null) operationType = 'create'
      else if (data.edited) {
        operationType = 'update'
      } else if (data.deleted) operationType = 'delete'

      if (operationType === null) continue

      payload.push({
        operation_type: operationType,
        certification_set_code: operationType === 'create' ? null : data.CertificationSetCode,
        certification_set_name: data.CertificationSetName,
        create_user_id: props.userId,
        create_data_time: null,
        update_user_id: props.userId,
        update_data_time: null,
        certification_code_list: data.CertificationCodeList.map((e) => certifications?.[e]?.CertificationCode)
      })
    }
    return payload
  }

  async function onSubmit() {
    const payload = prepareData(tableData)

    // create api request
    const init = {
      headers: {},
      body: {
        request: {
          corporation_number: props.corporationNumber,
          corporation_name: props.corporationName,
          user_sub: props.userId,
          user_name: props.userName,
          table_data: payload
        }
      }
    }

    try {
      const res = await httpPost(
        restApiKey.CHROFY_REST_API,
        PathInfoList.Resource.updateCertificationSet(props.corporationNumber),
        init
      )
      if (res?.message?.[0] !== 'Success') {
        throw new Error(CERT_SET_UPDATE_ERROR_MESSAGE)
      }
      const successMessage = 'データ反映を開始しました。'
      props.navigate('/master/masterList', { state: { message: successMessage } })
    } catch (error) {
      console.error(error)

      // 業務エラーは日本語をそのまま、英語は E011012 へ（バグ #3228）
      const displayMessage = error?.message === CERT_SET_UPDATE_ERROR_MESSAGE ? error.message : getMessage('E011012')
      setServerErrorMessage(displayMessage)
      errorMessageRef?.current.scrollIntoView()
      errorLog('E000001', props.userId, viewId.certificationInfo, 'certificationInfo', error)
    }
  }

  if (!isLoaded) return null

  return (
    <>
      <main className="page-container">
        <section className="page-content">
          <div className="centered-container">
            {/* 上階層に戻る */}
            <div className="bg main">
              <div className="back-upper-level py-8 pl-8 border-b border-blue2-lighten-20">
                <Link color="inherit" variant="button" underline="none" component={RouterLink} to="/master/masterList">
                  <span className="back-upper-level__link link--text hover:lighten-70 flex items-center">
                    <span className="icon-arrow-circle icon-32px mr-4 rotate-180 relative after:absolute after:bg-white after:w-full after:h-full after:rounded-full after:left-0 after:-z-10"></span>
                    <span className="text-h5 font-bold">{titleName.masterList}画面に戻る</span>{' '}
                  </span>
                </Link>
              </div>
            </div>

            <header className="page-header flex justify-center py-1">
              <h1 className="page-header__name text-h1 grey--text py-8">
                {masterClassification?.[master?.Classification]?.name || pageTitle}
              </h1>
            </header>

            <div ref={errorMessageRef}>
              <span style={{ color: 'red' }}>{serverErrorMessage}</span>
            </div>
            <div className="page-body">
              <div className="form-control">
                <div className="box-xy-bordered rounded-t-3xl rounded-b-2xl pt-10 pb-8 px-16 border-blue1">
                  <div className="content-area ">
                    <h2 className="box-bordered-heading blue1 white--text text-h3 font-medium pt-2.5 pb-3 px-14 -mt-10 -mx-16 rounded-t-2xl">
                      資格セット登録
                    </h2>
                    <div className="flex items-center justify-between my-6">
                      複数資格をまとめる資格セットを登録してください。
                      <br />
                      登録した資格セットはダッシュボード画面の表示条件設定で使用できます。
                    </div>
                    <div className="base-table-header ">
                      <div className="  flex justify-between gap-x-5 mb-3 items-center  btn-group !float-none">
                        <span className="text-body-2-medium grow  border-r border-blue2-lighten-20">
                          ■資格セット一覧
                        </span>
                        <Link
                          className="header-btn !p-0 "
                          color="inherit"
                          variant="button"
                          underline="none"
                          component={'button'}
                          onClick={handleAddSet}
                        >
                          <span className="header-btn white--text py-1.5 pl-7 pr-10 inline-flex items-center">
                            <span className="icon-plus-line icon-32px mr-1.5 my-px"></span>
                            <span className="-translate-y-px text-h7">{`新規登録`}</span>
                          </span>
                        </Link>
                      </div>
                    </div>

                    {tableData.length > 0 && (
                      <section className=" ">
                        <table className="base-table  table-fixed w-full">
                          <thead className="text-h7">
                            <tr>
                              {headers.map((x, index) => (
                                <th key={index} className={x.className}>
                                  {x.label}
                                </th>
                              ))}
                            </tr>
                          </thead>

                          <tbody className=" text-caption-1 text-sm">
                            {tableData.map((row, rowIndex) => {
                              const isDeleted = row.deleted
                              const deletedClass = isDeleted ? 'bg-gray-300 line-through ' : ''

                              return (
                                <tr
                                  key={`row${rowIndex}`}
                                  data-testid={`${row['CertificationSetName']}_${rowIndex}`}
                                  className={`${deletedClass}`}
                                >
                                  {headers.map((col, colIndex) => {
                                    return (
                                      <td
                                        className={`${col.className} ${colIndex === 0 && !isDeleted && 'title'}  `}
                                        key={`cell${colIndex}`}
                                      >
                                        {col.render ? col.render(row, rowIndex) : row[col.key]}
                                      </td>
                                    )
                                  })}
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </section>
                    )}
                    {tableData.length === 0 && <p className="text-center text-red-500 ">資格セット未登録です。</p>}

                    <footer className="content-footer ">
                      <div className="border-t border-blue1"></div>
                      <div className=" footer_button_area flex justify-around gap-x-5 mt-5">
                        <button
                          className="footer-btn button_back"
                          onClick={() => {
                            props.navigate('/master/masterList')
                          }}
                        >
                          キャンセル
                        </button>
                        <SpinnerButton
                          name="登録"
                          disabled={tableData.length === 0}
                          className="footer-btn button_base text-white"
                          onClick={onSubmit}
                        />
                      </div>
                    </footer>
                  </div>
                </div>
              </div>

              {master?.RelatedDashboard && (
                <RelatedDashboard dashboards={master?.RelatedDashboard} authority={props.authority} mode={props.mode} />
              )}
            </div>
          </div>
        </section>
      </main>
    </>
  )
}

const CertificationSet = () => {
  const state = useSelector((state) => state)
  const location = useLocation()

  const contentProps = {
    pageTitle: titleName.certificationSet + titleName.common,
    corporationNumber: state.company.corporationNumber,
    corporationName: state.company.name,
    userName: state.user.userName,
    userId: state.user.sub,
    isParent: state.company.isParent,
    message: location.state?.message,
    errorMessages: location.state?.errorMessages,
    authority: state.authority,
    mode: state.analysis.mode,
    navigate: useNavigate(),
    showToast: useToast(),
    showModal: useModal()
  }

  if (
    !state.authority.Master_AuthInfo.Availability ||
    !state.authority.Master_AuthInfo.Auth_Conf.Certification.CertificationSet_Set
  ) {
    return <Forbidden />
  }

  return (
    <>
      <CertificationSetContent {...contentProps} />
    </>
  )
}

export default CertificationSet
